'use client';

import React, { useState, useCallback } from 'react';
import { 
  Grid3X3Icon, 
  LayoutGridIcon, 
  CombineIcon, 
  Minimize2Icon,
  UploadIcon,
  PlusIcon
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { GridSplitTool } from '@/components/tools/GridSplitTool';
import { LayoutMosaicTool } from '@/components/tools/LayoutMosaicTool';
import { StitchTool } from '@/components/tools/StitchTool';
import { CompressTool } from '@/components/tools/CompressTool';

interface Tool {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
}

const tools: Tool[] = [
  {
    id: 'grid-split',
    name: '宫格拆图',
    description: '将图片均匀分割成多个小块',
    icon: <Grid3X3Icon className="h-6 w-6" />,
  },
  {
    id: 'layout-mosaic',
    name: '布局拼图',
    description: '用多张图片拼成创意布局',
    icon: <LayoutGridIcon className="h-6 w-6" />,
  },
  {
    id: 'stitch',
    name: '长图拼接',
    description: '纵向或横向拼接多张图片',
    icon: <CombineIcon className="h-6 w-6" />,
  },
  {
    id: 'compress',
    name: '图片压缩',
    description: '压缩图片文件大小，支持批量处理',
    icon: <Minimize2Icon className="h-6 w-6" />,
  },
];

export default function ImageWorkshop() {
  const [selectedTool, setSelectedTool] = useState<string>('grid-split');
  const [images, setImages] = useState<File[]>([]);
  const [imageUrls, setImageUrls] = useState<string[]>([]);

  const handleImageUpload = useCallback((files: FileList | null) => {
    if (!files || files.length === 0) return;
    
    const newImages: File[] = [];
    const newUrls: string[] = [];
    
    Array.from(files).forEach((file) => {
      if (file.type.startsWith('image/')) {
        newImages.push(file);
        newUrls.push(URL.createObjectURL(file));
      }
    });
    
    setImages((prev) => [...prev, ...newImages]);
    setImageUrls((prev) => [...prev, ...newUrls]);
  }, []);

  const handleToolChange = useCallback((toolId: string) => {
    setSelectedTool(toolId);
  }, []);

  const clearAll = useCallback(() => {
    imageUrls.forEach(url => URL.revokeObjectURL(url));
    setImages([]);
    setImageUrls([]);
  }, [imageUrls]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
      {/* 头部 */}
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60" role="banner">
        <div className="container mx-auto flex h-16 items-center justify-between px-4">
          <div className="flex items-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground" aria-hidden="true">
              <svg
                className="h-6 w-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
            <h1 className="text-xl font-bold">图片工坊</h1>
          </div>
          <nav className="flex items-center gap-6 text-sm" aria-label="页面导航">
            <span className="text-muted-foreground">免费在线图片处理工具箱</span>
          </nav>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8" role="main">
        {/* Hero Section */}
        <section className="mb-8 text-center" aria-labelledby="page-title">
          <h2 id="page-title" className="mb-2 text-3xl font-bold tracking-tight">图片工坊</h2>
          <p className="text-muted-foreground">免费在线图片处理工具，无需下载安装，保护您的隐私</p>
        </section>

        <div className="w-full">
          {/* 工具选择卡片 */}
          <section className="mb-8 pt-8" aria-labelledby="tools-heading">
            <h3 id="tools-heading" className="mb-6 text-2xl font-semibold">选择工具</h3>
            <div className="grid grid-cols-2 gap-4 xl:grid-cols-4" role="list">
              {tools.map((tool) => (
                <article
                  key={tool.id}
                  role="listitem"
                  aria-label={`${tool.name}工具：${tool.description}`}
                  className={`h-full cursor-pointer rounded-xl border border-border/80 bg-card/95 shadow-sm transition-all hover:shadow-md ${
                    selectedTool === tool.id
                      ? 'border-foreground/30 bg-muted/40'
                      : ''
                  }`}
                  onClick={() => handleToolChange(tool.id)}
                >
                  <CardHeader className="px-5 pb-2 pt-5">
                    <div className="mb-2 text-primary" aria-hidden="true">{tool.icon}</div>
                    <CardTitle className="text-lg font-semibold text-pretty">{tool.name}</CardTitle>
                  </CardHeader>
                  <CardContent className="px-5 pb-5">
                    <p className="min-h-10 text-sm leading-6 text-muted-foreground text-pretty">{tool.description}</p>
                  </CardContent>
                </article>
              ))}
            </div>
          </section>

          {/* 工具内容区域 */}
          <article className="min-h-[600px]" aria-labelledby="tool-content-heading">
            <Card>
              <CardHeader>
                <CardTitle id="tool-content-heading">
                  {tools.find((t) => t.id === selectedTool)?.name}
                </CardTitle>
              <CardDescription>
                {tools.find((t) => t.id === selectedTool)?.description}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* 图片上传区域 - 只有在没有图片时才显示 */}
              {images.length === 0 && (
                <div
                  className="mb-6 flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 transition-colors hover:border-primary/50"
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.add('border-primary', 'bg-primary/5');
                  }}
                  onDragLeave={(e) => {
                    e.currentTarget.classList.remove('border-primary', 'bg-primary/5');
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('border-primary', 'bg-primary/5');
                    handleImageUpload(e.dataTransfer.files);
                  }}
                >
                  <UploadIcon className="mb-4 h-12 w-12 text-muted-foreground" />
                  <p className="mb-2 text-lg font-medium">拖拽图片到这里，或点击上传</p>
                  <p className="mb-4 text-sm text-muted-foreground">
                    支持 JPG、PNG、GIF、WebP 等格式
                  </p>
                  <input
                    type="file"
                    id="file-upload"
                    className="hidden"
                    accept="image/*"
                    multiple
                    onChange={(e) => handleImageUpload(e.target.files)}
                  />
                  <Button asChild>
                    <label htmlFor="file-upload" className="cursor-pointer">
                      选择图片
                    </label>
                  </Button>
                </div>
              )}

              {/* 已上传图片预览 */}
              {imageUrls.length > 0 && (
                <div className="mb-6">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-sm font-medium">
                      已上传 {imageUrls.length} 张图片
                    </span>
                    <div className="flex items-center gap-2">
                      <input
                        type="file"
                        id="file-upload-more"
                        className="hidden"
                        accept="image/*"
                        multiple
                        onChange={(e) => handleImageUpload(e.target.files)}
                      />
                      <Button asChild variant="outline" size="sm">
                        <label htmlFor="file-upload-more" className="cursor-pointer">
                          <PlusIcon className="mr-1 h-4 w-4" />
                          添加更多
                        </label>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={clearAll}
                      >
                        清除全部
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-4 md:grid-cols-6 lg:grid-cols-8">
                    {imageUrls.map((url, index) => (
                      <div
                        key={index}
                        className="relative aspect-square overflow-hidden rounded-lg border bg-muted"
                      >
                        <img
                          src={url}
                          alt={`上传图片 ${index + 1}`}
                          className="h-full w-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 工具面板 */}
              {imageUrls.length > 0 && (
                <div className="mt-6" role="region" aria-label="工具操作面板">
                  {selectedTool === 'grid-split' && <GridSplitTool images={imageUrls} />}
                  {selectedTool === 'layout-mosaic' && <LayoutMosaicTool images={imageUrls} />}
                  {selectedTool === 'stitch' && <StitchTool images={imageUrls} />}
                  {selectedTool === 'compress' && <CompressTool images={images} />}
                </div>
              )}
            </CardContent>
          </Card>
          </article>
        </div>
      </main>

      {/* 页脚 */}
      <footer className="border-t bg-background/50 py-6" role="contentinfo">
        <div className="container mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>图片工坊 - 免费在线图片处理工具</p>
          <p className="mt-1">所有图片处理在本地浏览器完成，不会上传到服务器，保护您的隐私</p>
        </div>
      </footer>
    </div>
  );
}
